import { type Ref } from 'vue'
import * as THREE from 'three'
import { BATCHED_LAYER } from '@/utils/meshBatches'

const SELECT_DRAG_THRESHOLD = 5

export type PickingOptions = {
  container: Ref<HTMLDivElement | undefined>
  getCamera: () => THREE.PerspectiveCamera | null
  flushWorldMatrices: () => void
  getPickTargets: () => THREE.Object3D[]
  resolveHit: (hit: THREE.Intersection) => THREE.Mesh | null
  isEnabled: () => boolean
  isBlocked: () => boolean
  onHover: (mesh: THREE.Mesh | null) => void
  onSelect: (mesh: THREE.Mesh | null) => void
}

export function useViewerPicking(opts: PickingOptions) {
  const raycaster = new THREE.Raycaster()
  raycaster.layers.enable(BATCHED_LAYER)
  const ndc = new THREE.Vector2()
  const hits: THREE.Intersection[] = []

  let pointerDownPos: { x: number; y: number } | null = null
  let pendingHover: { x: number; y: number } | null = null
  let hoverRafId: number | null = null

  function pickMesh(clientX: number, clientY: number): THREE.Mesh | null {
    const camera = opts.getCamera()
    const el = opts.container.value
    if (!camera || !el || !opts.isEnabled()) return null
    const targets = opts.getPickTargets()
    if (targets.length === 0) return null

    opts.flushWorldMatrices()
    const rect = el.getBoundingClientRect()
    ndc.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    )
    raycaster.setFromCamera(ndc, camera)
    hits.length = 0
    raycaster.intersectObjects(targets, false, hits)
    for (const hit of hits) {
      const mesh = opts.resolveHit(hit)
      if (mesh) return mesh
    }
    return null
  }

  function onPointerMove(e: PointerEvent) {
    if (!opts.isEnabled() || pointerDownPos || opts.isBlocked()) return
    pendingHover = { x: e.clientX, y: e.clientY }
    if (hoverRafId !== null) return
    hoverRafId = window.requestAnimationFrame(() => {
      hoverRafId = null
      const at = pendingHover
      pendingHover = null
      if (at) opts.onHover(pickMesh(at.x, at.y))
    })
  }

  function cancelPendingHover() {
    if (hoverRafId !== null) window.cancelAnimationFrame(hoverRafId)
    hoverRafId = null
    pendingHover = null
  }

  function onPointerLeave() {
    cancelPendingHover()
    opts.onHover(null)
  }

  function onPointerDown(e: PointerEvent) {
    if (!opts.isEnabled()) return
    pointerDownPos = { x: e.clientX, y: e.clientY }
  }

  function onPointerUp(e: PointerEvent) {
    if (!pointerDownPos) return
    const moved = Math.hypot(e.clientX - pointerDownPos.x, e.clientY - pointerDownPos.y)
    pointerDownPos = null
    if (moved > SELECT_DRAG_THRESHOLD) return
    // A click that landed on a gizmo handle is handled by the gizmo, not a selection.
    if (opts.isBlocked()) return
    opts.onSelect(pickMesh(e.clientX, e.clientY))
  }

  function attach() {
    const el = opts.container.value
    if (!el) return
    el.addEventListener('pointerdown', onPointerDown)
    el.addEventListener('pointerup', onPointerUp)
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerleave', onPointerLeave)
  }

  function detach() {
    const el = opts.container.value
    el?.removeEventListener('pointerdown', onPointerDown)
    el?.removeEventListener('pointerup', onPointerUp)
    el?.removeEventListener('pointermove', onPointerMove)
    el?.removeEventListener('pointerleave', onPointerLeave)
    cancelPendingHover()
    pointerDownPos = null
  }

  return { pickMesh, attach, detach, cancelPendingHover }
}
